import "./CallDialogs.css";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useContextStateManagement } from "../../context/ContextApi";

const fmtSeconds = (secs) => {
  const n = parseInt(secs, 10);
  if (!Number.isFinite(n) || n < 0) return "—";
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const ROLE_COLOR = {
  inbound: "primary",
  outbound: "primary",
  "sip-bridge": "info",
  trunking: "warning",
  transfer: "warning",
  leg: "default",
};

/**
 * Generic call-legs dialog. Pass either a fully-loaded `group` object or just
 * a `rootCallSid` and the dialog will fetch /api/callGroupDetails on open.
 *
 * Props:
 *   open          boolean
 *   onClose       () => void
 *   rootCallSid   string (preferred)
 *   group         optional pre-loaded group object {legs, totalSeconds, billableSeconds, hasTransfer, transferLinks}
 */
const CallLegsDialog = ({ open, onClose, rootCallSid, group: initialGroup }) => {
  const { PracticeId, token, dateRangeRequest } = useContextStateManagement();
  const [group, setGroup] = useState(initialGroup || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    if (initialGroup && Array.isArray(initialGroup.legs) && initialGroup.legs.length > 0) {
      setGroup(initialGroup);
      return undefined;
    }
    if (!rootCallSid || !token || !PracticeId || !dateRangeRequest) return undefined;

    let cancelled = false;
    setLoading(true);
    setError("");
    dateRangeRequest(token, "/api/callGroupDetails", { rootCallSid, PracticeId })
      .then((data) => {
        if (cancelled) return;
        if (data && typeof data === "object" && (data.legs || data.rootCallSid)) {
          setGroup(data);
        } else if (typeof data === "string") {
          setError(data || "Failed to load call details");
        } else {
          setError("Failed to load call details");
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load call details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, rootCallSid, initialGroup, token, PracticeId, dateRangeRequest]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          m: 2,
          top: "auto !important",
          left: "auto !important",
          position: "relative !important",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center !important",
          justifyContent: "center !important",
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Call legs
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {!loading && error && (
          <Typography color="error">{error}</Typography>
        )}
        {!loading && !error && group && (
          <>
            <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap">
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Root Call SID
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {group.rootCallSid || "—"}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Total (customer on the line)
                </Typography>
                <Typography variant="body1">
                  {fmtSeconds(group.totalSeconds)}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Telephony (Twilio: per-leg, rounded up)
                </Typography>
                <Typography variant="body1">
                  {(() => {
                    const legs = Array.isArray(group.legs) ? group.legs : [];
                    let mins;
                    if (legs.length > 0) {
                      mins = legs.reduce(
                        (s, l) =>
                          s + Math.max(0, Math.ceil((Number(l?.durationSec) || 0) / 60)),
                        0,
                      );
                    } else {
                      mins = Math.max(
                        0,
                        Math.ceil((Number(group.billableSeconds) || 0) / 60),
                      );
                    }
                    return `${mins} min`;
                  })()}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Legs
                </Typography>
                <Typography variant="body1">{group.legCount ?? group.legs?.length ?? 0}</Typography>
              </Stack>
              {group.hasTransfer && (
                <Chip label="Transfer occurred" color="warning" size="small" />
              )}
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>From → To</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>SID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(group.legs || []).map((leg, idx) => (
                  <TableRow key={leg.sid || idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={leg.role || "leg"}
                        color={ROLE_COLOR[leg.role] || "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <div style={{ wordBreak: "break-all" }}>
                        <span>{leg.from || "—"}</span>
                        <span style={{ margin: "0 6px", opacity: 0.6 }}>→</span>
                        <span>{leg.to || "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {leg.startedAt
                        ? moment(leg.startedAt).format("MM/DD HH:mm:ss")
                        : "—"}
                    </TableCell>
                    <TableCell>{fmtSeconds(leg.durationSec)}</TableCell>
                    <TableCell>{leg.status || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11, maxWidth: 200 }}>
                      <span style={{ wordBreak: "break-all" }}>{leg.sid || "—"}</span>
                    </TableCell>
                  </TableRow>
                ))}
                {(group.legs || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No legs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallLegsDialog;


